// src/components/forms/shared/FieldHelperText.tsx
/**
 * Reusable component for displaying helper text with animated error icon
 * Used by StyledTextField, DatePicker, RadioButtonGroup, and other form elements
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';

type HelperTextTone =
  'error' | 'warning' | 'info' | 'success' | 'primary' | 'secondary';

interface FieldHelperTextProps {
  helperText?: string;
  error?: boolean;
  action?: React.ReactNode;
  /** Set false to drop the error icon and its pulse. Defaults to true. */
  showIcon?: boolean;
  /** Centre the text. Defaults to left-aligned. */
  centered?: boolean;
  /** Theme palette key for the text colour. Defaults to the error/neutral colours. */
  color?: HelperTextTone;
  /** Overrides the font weight. Defaults to bold for errors, medium otherwise. */
  fontWeight?: number | string;
  /** Honour line breaks in the text. Defaults to collapsing them. */
  multiline?: boolean;
  /** Overrides the font size. Defaults to 2rem for errors, 1.8rem otherwise. */
  fontSize?: string;
}

const FieldHelperText: React.FC<FieldHelperTextProps> = ({
  helperText,
  error = false,
  action,
  showIcon = true,
  centered = false,
  color,
  fontWeight,
  multiline = false,
  fontSize,
}) => {
  const theme = useTheme();

  if (!helperText) {
    return null;
  }

  const toneColor = color
    ? theme.palette.mode === 'dark'
      ? theme.palette[color].light
      : theme.palette[color].main
    : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        mt: '0.5rem',
        ml: centered ? 0 : '0.5rem',
        ...(centered ? { justifyContent: 'center', textAlign: 'center' } : {}),
        ...(action ? { flexWrap: 'wrap' } : {}),
      }}
    >
      {error && showIcon && (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              border: `3px solid ${
                theme.palette.mode === 'dark'
                  ? theme.palette.error.light
                  : theme.palette.error.main
              }`,
              backgroundColor: 'transparent',
              opacity: 0.8,
              animation: 'radiate 1s ease-out infinite',
            },
            '@keyframes radiate': {
              '0%': {
                transform: 'translate(-50%, -50%) scale(0.5)',
                opacity: 0.8,
              },
              '100%': {
                transform: 'translate(-50%, -50%) scale(2.5)',
                opacity: 0,
              },
            },
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: '2rem',
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.error.light
                  : theme.palette.error.main,
              position: 'relative',
              zIndex: 1,
            }}
          />
        </Box>
      )}
      <Typography
        sx={{
          fontSize: fontSize ?? (error ? '2rem' : '1.8rem'),
          fontWeight: fontWeight ?? (error ? 700 : 500),
          m: 0,
          ...(multiline ? { whiteSpace: 'pre-line' } : {}),
          color:
            toneColor ??
            (theme.palette.mode === 'dark'
              ? error
                ? theme.palette.error.light
                : theme.palette.grey[400]
              : error
                ? theme.palette.error.main
                : theme.palette.text.secondary),
        }}
      >
        {helperText}
      </Typography>
      {action}
    </Box>
  );
};

export default FieldHelperText;
